import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Droplets, ShoppingBag, Users, Settings as Cog,
  MessageCircle, Utensils, Dumbbell, Heart, LogOut, Smartphone, Menu, Star, Youtube, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CartDrawer } from "@/components/CartDrawer";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const MAIN = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/bloodwork", label: "Bloodwork", icon: Droplets },
  { to: "/app/shop", label: "Shop", icon: ShoppingBag },
  { to: "/videos", label: "Videos", icon: Youtube },
  { to: "/app/mentor", label: "Mentor", icon: Users },
  { to: "/app/settings", label: "Settings", icon: Cog },
];
const APP_ONLY = [
  { to: "/app/clara", label: "Clára AI", icon: MessageCircle },
  { to: "/app/meals", label: "Meal Tracking", icon: Utensils },
  { to: "/app/workouts", label: "Workouts", icon: Dumbbell },
  { to: "/app/social", label: "Circles", icon: Heart },
];

function NavItem({ item, onNavigate, suffix = "", badge = 0 }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} onClick={onNavigate} data-testid={`nav-${item.label.toLowerCase().split(" ")[0]}${suffix}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? "bg-brand-wash text-brand-dark" : "text-muted hover:bg-rail hover:text-ink"
        }`}>
      <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
      <span className="flex-1">{item.label}</span>
      {badge > 0 && (
        <span data-testid={`nav-badge-${item.label.toLowerCase()}`} className="grid h-5 min-w-[20px] place-items-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

function SidebarBody({ user, onLogout, onNavigate, idSuffix = "" }) {
  const { data: proposalCount = 0 } = useQuery({
    queryKey: ["incoming-proposals-count"], retry: false, refetchInterval: 60000,
    queryFn: async () => { try { const d = (await api.get("/mentor/proposals/incoming/count")).data; return d.pending ?? d.count ?? 0; } catch { return 0; } },
  });
  const { data: isShopAdmin = false } = useQuery({
    queryKey: ["is-shop-admin", user?.email], retry: false, staleTime: 300000,
    queryFn: async () => { try { await api.get("/shop/admin/orders/counts"); return true; } catch { return false; } },
  });
  const { data: isPlatformAdmin = false } = useQuery({
    queryKey: ["is-platform-admin", user?.email], retry: false, staleTime: 300000,
    queryFn: async () => { try { const d = (await api.get("/social/admin/me/is_platform_admin")).data; return !!d.is_admin; } catch { return false; } },
  });
  return (
    <>
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5 px-6 py-5">
        <img src="/better-days-mark.png" alt="Better Days" className="h-6 w-auto" />
        <span className="text-[15px] font-bold tracking-tight text-ink">Better Days</span>
      </Link>
      <nav className="flex-1 space-y-1 px-3 py-2">
        {MAIN.map((i) => <NavItem key={i.to} item={i} onNavigate={onNavigate} suffix={idSuffix} badge={i.to === "/app/mentor" ? proposalCount : 0} />)}
        {isShopAdmin && (
          <NavItem item={{ to: "/app/admin/reviews", label: "Reviews", icon: Star }} onNavigate={onNavigate} suffix={idSuffix} />
        )}
        {(isShopAdmin || isPlatformAdmin) && (
          <NavItem item={{ to: "/app/admin", label: "Admin", icon: ShieldCheck }} onNavigate={onNavigate} suffix={idSuffix} />
        )}
        <p className="px-3 pb-2 pt-6 text-[11px] font-semibold uppercase tracking-widest text-line-strong">In the app</p>
        {APP_ONLY.map((i) => (
          <NavLink key={i.to} to={i.to} onClick={onNavigate} data-testid={`nav-${i.label.toLowerCase().split(" ")[0]}${idSuffix}`}
            className={({ isActive }) =>
              `group flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive ? "bg-brand-wash text-brand-dark" : "text-muted hover:bg-rail hover:text-ink"}`}>
            <span className="flex items-center gap-3"><i.icon className="h-[18px] w-[18px]" /> {i.label}</span>
            <Smartphone className="h-3.5 w-3.5 text-line-strong" />
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-line p-3">
        <div className="flex items-center gap-3 rounded-md px-3 py-2">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-ink text-xs font-bold text-white">
            {(user?.name || "U").slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{user?.name}</p>
            <p className="truncate text-xs text-muted">Member</p>
          </div>
          <button onClick={onLogout} data-testid={`logout-btn${idSuffix}`} className="text-muted hover:text-danger"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
    </>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const doLogout = () => { setMobileOpen(false); logout(); navigate("/"); };
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-surface">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-line bg-card lg:flex">
        <SidebarBody user={user} onLogout={doLogout} />
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line bg-card/90 px-5 py-3 backdrop-blur-md">
          <div className="flex items-center gap-2 lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" aria-label="Open menu"
                  className="rounded-md border border-line p-2 text-ink hover:bg-rail">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="flex w-72 flex-col p-0" data-testid="mobile-nav-drawer">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SidebarBody user={user} onLogout={doLogout} onNavigate={closeMobile} idSuffix="-m" />
              </SheetContent>
            </Sheet>
            <img src="/better-days-mark.png" alt="Better Days" className="h-5 w-auto" />
            <span className="font-bold tracking-tight text-ink">Better Days</span>
          </div>
          <div className="hidden text-sm text-muted lg:block">Web Companion — synced with your mobile app</div>
          <div className="flex items-center gap-3">
            <CartDrawer />
          </div>
        </header>
        <main className="p-5 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
